import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export type ButtonVariant = 'cta' | 'primary' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    font-size: 13px;
    padding: 8px 18px;
    min-inline-size: 44px;
  `,
  md: css`
    font-size: var(--text-md);
    padding: 12px 24px;
    min-width: 170px;
  `,
  lg: css`
    font-size: 18px;
    padding: 15px 34px;
    min-width: 200px;
  `,
};

const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  cta: css`
    background: var(--accent-cta);
    color: var(--white);
    border: 2px solid transparent;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--accent-cta-hover);
        color: var(--white);
      }
    }
  `,
  primary: css`
    background: var(--brand);
    color: var(--white);
    border: 2px solid transparent;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--brand-hover);
        color: var(--white);
      }
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--green-700);
    border: 2px solid var(--green-500);

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--green-500);
        color: var(--white);
      }
    }
  `,
  link: css`
    background: transparent;
    color: var(--accent-link);
    border: 2px solid transparent;
    min-inline-size: 44px;
    padding: 6px 4px;
    border-radius: 0;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--accent-link);
      }
    }
  `,
};

const baseButtonStyles = css<{ $variant: ButtonVariant; $size: ButtonSize; $wobble: boolean }>`
  min-block-size: 44px;
  min-inline-size: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  line-height: 1;
  border-radius: var(--radius-pill);
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color var(--duration-fast) ease,
    color var(--duration-fast) ease;
  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      animation: none;
    }
  }

  &:active {
    opacity: 0.85;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledButton = styled.button<{ $variant: ButtonVariant; $size: ButtonSize; $wobble: boolean }>`
  ${baseButtonStyles}
`;

const StyledAnchor = styled.a<{ $variant: ButtonVariant; $size: ButtonSize; $wobble: boolean }>`
  ${baseButtonStyles}
`;

interface CommonProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  wobble?: boolean;
  className?: string;
}

type ButtonAsButton = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { href?: undefined };
type ButtonAsAnchor = CommonProps & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & { href: string };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

export function Button({ children, variant = 'cta', size = 'md', wobble: wobbleOnHover = false, className, ...rest }: ButtonProps) {
  if ('href' in rest && rest.href) {
    const { href, ...anchorRest } = rest as AnchorHTMLAttributes<HTMLAnchorElement>;

    return (
      <StyledAnchor $variant={variant} $size={size} $wobble={wobbleOnHover} className={className} href={href} {...anchorRest}>
        {children}
      </StyledAnchor>
    );
  }

  const buttonRest = rest as ButtonHTMLAttributes<HTMLButtonElement>;

  return (
    <StyledButton $variant={variant} $size={size} $wobble={wobbleOnHover} className={className} type="button" {...buttonRest}>
      {children}
    </StyledButton>
  );
}
